import { forwardRef, useEffect, useRef, useState } from 'react'

import Style from './style'

import levelSound from 'shared/asset/level.mp3'
import warningSound from 'shared/asset/warning.mp3'
import useLogoByBackground from 'shared/hooks/useLogoByBackground'
import {
	isAdminIntervention,
	isBreakTransition,
	isGameActive,
	isNaturalLevelProgression,
	shouldPlayLevelSound,
} from 'shared/lib/levelSoundUtils'
import { joinElection } from 'shared/lib/audioLeader'
import { createSoundPlayer, unlockAudio } from 'shared/lib/soundPlayer'
import useLogoStorage from 'shared/model/useLogoStorage'
import useThemeCookie, { GRADATION } from 'shared/model/useThemeCookie'
import { useParams } from 'react-router-dom'
import PrizeAutoScroll from 'shared/ui/PrizeAutoScroll'
import SettingModal from '../Normal/ui/SettingModal'
import { OFFICIAL } from 'shared/constant/lookup/eventType'

const Wrapper = forwardRef(
	(
		{
			children,
			title,
			infoData,
			logoImg,
			timeData,
			countDown,
			eventData,
			prizeData,
			prize_editor,
			prize_list,
			duration,
			navigationBlock,
			setNavigationBlock,
			navigateWithConfirm,
		},
		ref,
	) => {
		const  event_type_flag  = eventData?.data?.event_type_flag
		const { idx } = useParams()
		const [themeModalOpen,setThemeModalOpen] = useState(false)
		const lastLevelUpStateRef = useRef({level:null,wasLevelUp:false})

		// Audio: only one tab per event plays sounds (leader election), and only
		// when the tab is visible. This eliminates the "Blind Up fires ~10s late
		// / fires again on a second tab" bug where a throttled background tab's
		// level-up transition runs long after the real boundary.
		const playerRef = useRef(null)
		if (playerRef.current === null) playerRef.current = createSoundPlayer()
		const isLeaderRef = useRef(false)

		// Claim leadership when this tab becomes visible, so the active tab
		// always owns audio for the event.
		useEffect(() => {
			const eventId = idx
			if (!eventId) return

			const election = joinElection(eventId, (isLeader) => {
				isLeaderRef.current = isLeader
				if (!isLeader) {
					// Lost leadership: kill any in-flight sound immediately.
					playerRef.current?.stopAll()
				}
			})

			const onVisibilityChange = () => {
				if (document.visibilityState === 'visible') {
					unlockAudio()
					election.claim()
				} else {
					// Hidden: release any sounding audio so a late, throttled
					// callback doesn't emit it.
					playerRef.current?.stopAll()
				}
			}

			if (document.visibilityState === 'visible') {
				unlockAudio()
				election.claim()
			}
			document.addEventListener('visibilitychange', onVisibilityChange)

			return () => {
				document.removeEventListener('visibilitychange', onVisibilityChange)
				election.release()
				playerRef.current?.stopAll()
			}
		}, [idx])

		// Preload sounds once.
		useEffect(() => {
			playerRef.current?.load('level', levelSound)
			playerRef.current?.load('warning', warningSound)
		}, [])

		const [themeCookie] = useThemeCookie()
		const [logoStorage,setLogoStorage] = useLogoStorage()
		const autoLogo = useLogoByBackground()
		const reEntry =
			`${infoData?.find((elem) => elem?.label === 'Re-Entry')?.data}` || '0'
		const early =
			`${infoData?.find((elem) => elem?.label === 'Early')?.data}` || '0'
		const addOn =
			`${infoData?.find((elem) => elem?.label === 'Add-on')?.data}` || '0'

		useEffect(() => {
			if (!isGameActive(timeData)) return

			const currentLevel = timeData.level
			const lastState = lastLevelUpStateRef.current
			const shouldPlay = shouldPlayLevelSound(timeData,lastState)

			// Audio guards: only the leader tab, and only when visible.
			const canPlayAudio =
				isLeaderRef.current &&
				typeof document !== 'undefined' &&
				document.visibilityState === 'visible'

			if (shouldPlay) {
				if (canPlayAudio) {
					console.log(
						'🚀 Level up sound playing - Level:',
						lastState.level,
						'->',
						currentLevel,
						'| leader:',
						isLeaderRef.current,
					)
					playerRef.current?.play('level')
				} else {
					console.log('🚀 Level up sound skipped (not leader/hidden)', {
						isLeader: isLeaderRef.current,
						visible:
							typeof document !== 'undefined'
								? document.visibilityState
								: 'n/a',
					})
				}
			} else if (
				timeData.is_level_up &&
				lastState.level !== currentLevel &&
				lastState.level !== null
			) {
				console.log('🚀 Level up blocked:', {
					transition: `${lastState.level} -> ${currentLevel}`,
					reasons: {
						isBreakTime: isBreakTransition(currentLevel,lastState.level),
						isAdminAction: isAdminIntervention(currentLevel,lastState.level),
						isFinalLevel:
							timeData.next_level === null || timeData.next_level === '0',
						isNaturalProgression: isNaturalLevelProgression(
							currentLevel,
							lastState.level,
						),
						flagTransition: `${lastState.wasLevelUp} -> ${timeData.is_level_up}`,
					},
				})
			}

			// Update tracking state
			lastLevelUpStateRef.current = {
				level: currentLevel,
				wasLevelUp: timeData.is_level_up,
			}
		}, [timeData])

		// Warning sound effect for last 5 seconds
		useEffect(() => {
			if (!countDown || timeData?.level === 'break') return

			// Only the leader tab plays audio; never when hidden (a throttled
			// background tab would fire the warning late, after the boundary).
			if (
				!isLeaderRef.current ||
				(typeof document !== 'undefined' &&
					document.visibilityState !== 'visible')
			) {
				return
			}

			const split = countDown.split(':')
			const minutes = parseInt(split[0])
			const seconds = parseInt(split[1])

			// Play warning sound every second during last 5 seconds (00:05 to 00:01)
			if (minutes === 0 && seconds <= 5 && seconds >= 1) {
				playerRef.current?.play('warning')
			}
		}, [countDown, timeData?.level])

		const isPaused =
			timeData?.pause ||
			(timeData?.next_level === '0' && countDown === '00:00')
		const isBreak = timeData?.level === 'break'
		return (
			<>
				{themeModalOpen && (
					<SettingModal
						closeEvent={() => setThemeModalOpen(false)}
						setLogoStorage={setLogoStorage}
						logoStorage={logoStorage}
						logoImg={logoImg}
						navigationBlock={navigationBlock}
						setNavigationBlock={setNavigationBlock}
					/>
				)}

				{isPaused && (
					<Style.PauseCover $isBreak={isBreak}>
						<p>PAUSE</p>
					</Style.PauseCover>
				)}

				<Style.Wrapper ref={ref} $themeType={themeCookie}>
					{themeCookie === GRADATION && (
						<>
							<Style.Circle1 />
							<Style.Circle2 />
							<Style.Circle3 />
							<Style.Circle4 />
							<Style.ThemeBg />
						</>
					)}
					{isBreak ? (
						<>
							<Style.BreakTitleDiv $themeType={themeCookie}>
								<button onClick={() => navigateWithConfirm('/select')}>
									<Style.BackIcon $themeType={themeCookie}>←</Style.BackIcon>
								</button>
								<Style.BreakTitleContainer>
									<Style.BreakTitle $themeType={themeCookie}>
										<h2>BREAK TIME</h2>
										<p>
											<span>{countDown || '00:00'}</span>
										</p>
									</Style.BreakTitle>
									<Style.BreakSubTitle $themeType={themeCookie}>
										{timeData?.next_level === 'break' ? (
											<h2>NEXT BLINDS BREAK TIME</h2>
										) : (
											<h2>
												NEXT BLINDS {timeData?.next_sb} / {timeData?.next_bb} (
												{timeData?.next_ante})
											</h2>
										)}
										{timeData?.level_after_break ? (
											<>
												<h2>NEXT LEVEL: {timeData.level_after_break}</h2>
												{/* <h2>BLINDS {timeData.level_after_break_sb} / {timeData.level_after_break_bb} ({timeData.level_after_break_ante})</h2> */}
											</>
										) : (
											<></>
										)}
									</Style.BreakSubTitle>
								</Style.BreakTitleContainer>
								<button>
									<Style.SettingIcon
										$themeType={themeCookie}
										onClick={() => setThemeModalOpen(true)}
									/>
								</button>
							</Style.BreakTitleDiv>
						</>
					) : (
						<Style.TitleDiv $themeType={themeCookie}>
							<button onClick={() => navigateWithConfirm('/select')}>
								<Style.BackIcon $themeType={themeCookie}>←</Style.BackIcon>
							</button>
							<h2>{title}</h2>
							<button>
								<Style.SettingIcon
									$themeType={themeCookie}
									onClick={() => setThemeModalOpen(true)}
								/>
							</button>
						</Style.TitleDiv>
					)}

					<Style.ContentDiv>{children}</Style.ContentDiv>

					<Style.FooterDiv $themeType={themeCookie}>
						{infoData.map((elem,index) => {
							if (
								elem?.label === 'Re-Entry' ||
								elem?.label === 'Early' ||
								elem?.label === 'Add-on'
							)
								return null
								
							if (elem?.label === 'Re-Entry / Early / Add-on') {

								if (event_type_flag === OFFICIAL) {
									if(!eventData?.data?.early_chip && !eventData?.data?.add_on_chip) return null
									return (
										<Style.FooterInfo key={index}>
										<h2>
											{eventData?.data?.early_chip ? '  Early' : null}{' '}
											{eventData?.data?.add_on_chip ? ' / Add-on' : null}
										</h2>
										<p>
											{eventData?.data?.early_chip ? `  ${early}` : null}{' '}
											{eventData?.data?.add_on_chip ? ` / ${addOn}` : null}
										</p>
									</Style.FooterInfo>
									)
								}

								return (
									<Style.FooterInfo key={index}>
										<h2>
											Re-Entry {eventData?.data?.early_chip  ? ' / Early' : null}{' '}
											{eventData?.data?.add_on_chip ? ' / Add-on' : null}
										</h2>
										<p>
											{reEntry}{' '}
											{eventData?.data?.early_chip ? ` / ${early}` : null}{' '}
											{eventData?.data?.add_on_chip ? ` / ${addOn}` : null}
										</p>
									</Style.FooterInfo>
								)
							}
							return (
								<Style.FooterInfo key={index}>
									<h2>{elem?.label}</h2>
									<p>{elem?.data}</p>
								</Style.FooterInfo>
							)
						})}

						<Style.LogoDiv
							$logoWidth={logoStorage?.width}
							$logoHeight={logoStorage?.height}
						>
							<img src={autoLogo} alt='Logo' />
						</Style.LogoDiv>
					</Style.FooterDiv>

					{!isBreak && (
						<Style.ResponsivePrizeDiv $themeType={themeCookie}>
							<Style.PrizeListDiv>
								<h2>Prize Pool</h2>
								<PrizeAutoScroll prize_editor={prize_editor || []} speed={40} />
							</Style.PrizeListDiv>
							{prizeData?.total && (
								<Style.TotlePrizeDiv>
									<h2>Total Prize</h2>
									<p>{prizeData?.total}</p>
								</Style.TotlePrizeDiv>
							)}
							<Style.ResponsiveLogoDiv
								$logoWidth={logoStorage?.width}
								$logoHeight={logoStorage?.height}
							>
								<img src={autoLogo} alt='Logo' />
							</Style.ResponsiveLogoDiv>
						</Style.ResponsivePrizeDiv>
					)}
				</Style.Wrapper>
			</>
		)
	},
)

Wrapper.displayName = 'Wrapper'

export default Wrapper
